import { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiClient } from '../services/apiClient';
import {
  Period,
  generatePeriodOptions,
  periodToDateRange,
  periodToGroupBy,
} from '../utils/dateRange';
import SimUsageGraph, { UsageBucket } from '../components/SimUsageGraph';
import { Row, Col, Select, Card, Empty, Table, Tag, Input, Spin, Typography } from 'antd';
import type { Breakpoint } from 'antd';
import {
  CloudDownloadOutlined,
  WifiOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import PageHelp from '../components/PageHelp';
import PageHeader from '../components/PageHeader';
import StatCardGrid, { StatCardItem } from '../components/StatCardGrid';
import EditableFriendlyName from '../components/EditableFriendlyName';
import { useDisplaySettings } from '../theme/DisplaySettingsContext';
import { blurStyle } from '../utils/redact';
import { formatKilobytes } from '../utils/formatUsage';
import { getSims } from '../services/adminService';

const { Option } = Select;
const { Text } = Typography;

function buildIccidParam(
  searchText: string,
  simCards: VrsSimCard[],
  friendlyByIccid: Record<string, { assignmentId: number; friendlyName: string | null }>
): string {
  if (!searchText) return '';
  const q = searchText.toLowerCase();
  const matched = simCards
    .filter(
      s =>
        s.iccid.toLowerCase().includes(q) ||
        (s.alias1 || '').toLowerCase().includes(q) ||
        (friendlyByIccid[s.iccid]?.friendlyName || '').toLowerCase().includes(q)
    )
    .map(s => s.iccid);
  return matched.length > 0 ? `&iccids=${matched.join(',')}` : '';
}

/** Raw shape returned by /vrs/usage/buckets — data only, volume in bytes */
type VrsBucketRaw = { period: string; total_data_bytes: number };

/** Normalise a VRS bucket to the UsageBucket shape the graph expects.
 *  Converts bytes → KB for total_data_volume; zero-fills call/SMS fields
 *  since VRS SIMs carry data only. */
const normalizeVrsBucket = (b: VrsBucketRaw): UsageBucket => ({
  period: b.period,
  call_count: 0,
  sms_count: 0,
  data_count: 0,
  total_call_duration: 0,
  total_data_volume: b.total_data_bytes / 1024,
});

type VrsUsageSummary = {
  iccid: string;
  msisdn: string;
  alias1: string | null;
  status: string;
  total_data_mb: string;
  total_data_gb: string;
  total_data_bytes: number;
  days_with_usage: number;
  first_usage: string;
  last_usage: string;
};

type VrsSimCard = {
  iccid: string;
  msisdn: string;
  alias1: string | null;
  status: string;
};

function VrsUsage() {
  const auth = useAuth();

  const [summary, setSummary] = useState<VrsUsageSummary[]>([]);
  const { redactNames } = useDisplaySettings();
  const [simCards, setSimCards] = useState<VrsSimCard[]>([]);
  const [buckets, setBuckets] = useState<UsageBucket[]>([]);
  const [friendlyByIccid, setFriendlyByIccid] = useState<
    Record<string, { assignmentId: number; friendlyName: string | null }>
  >({});
  const [loading, setLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [period, setPeriod] = useState<Period>('thisMonth');
  const [searchText, setSearchText] = useState('');
  const [printMode, setPrintMode] = useState(false);

  const isFirstRender = useRef(true);
  const graphAbortRef = useRef<AbortController | null>(null);
  const graphDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PERIOD_OPTIONS = useMemo(() => generatePeriodOptions(), []);

  // Main effect: fires on period/auth change.
  // Phase 1 fetches summary + sim metadata in parallel so tiles and table update quickly.
  // Phase 2 fetches the graph with the current search filter applied to the fresh sim data.
  useEffect(() => {
    if (!auth.authenticated || !auth.token || auth.isLoading) return;

    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {
      setLoading(true);
      setGraphLoading(true);
      try {
        const { startDate, endDate } = periodToDateRange(period);
        const dateParams = startDate ? `?startDate=${startDate}&endDate=${endDate}` : '';

        // Phase 1: summary + sim cards + friendly names
        const [summaryRes, simRes, allocRes] = await Promise.all([
          ApiClient.get<{ data: VrsUsageSummary[] }>(`/vrs/usage/summary${dateParams}`, signal),
          ApiClient.get<{ data: VrsSimCard[] }>('/vrs/sim-cards', signal),
          getSims({ provider: 'vrs' }).catch(() => []),
        ]);

        const cards = simRes.data || [];
        setSummary(summaryRes.data || []);
        setSimCards(cards);

        const map: Record<string, { assignmentId: number; friendlyName: string | null }> = {};
        for (const sim of allocRes) {
          if (sim.allocation) {
            map[sim.identifier] = {
              assignmentId: sim.allocation.assignmentId,
              friendlyName: sim.allocation.friendlyName,
            };
          }
        }
        setFriendlyByIccid(map);
        setLoading(false);

        // Phase 2: graph with current search filter applied to fresh data.
        // searchText intentionally read from closure — search effect handles its own updates.
        const groupBy = periodToGroupBy(period);
        const iccidParam = buildIccidParam(searchText, cards, map);
        const bucketRes = await ApiClient.get<{ buckets: VrsBucketRaw[] }>(
          `/vrs/usage/buckets?startDate=${startDate}&endDate=${endDate}&period=${groupBy}${iccidParam}`,
          signal
        );
        setBuckets(
          Array.isArray(bucketRes.buckets) ? bucketRes.buckets.map(normalizeVrsBucket) : []
        );
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('VRS Usage fetch error:', err);
      } finally {
        setLoading(false);
        setGraphLoading(false);
      }
    };

    void load();
    return () => controller.abort();
    // eslint-disable-next-line
  }, [auth.authenticated, auth.token, auth.isLoading, period]);

  // Debounced graph re-fetch when search text changes.
  // Skipped on initial mount (main effect handles the first graph load).
  // AbortController cancels any in-flight request when the user keeps typing.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!auth.authenticated || !auth.token) return;

    if (graphDebounceRef.current) clearTimeout(graphDebounceRef.current);

    graphDebounceRef.current = setTimeout(async () => {
      if (graphAbortRef.current) graphAbortRef.current.abort();
      graphAbortRef.current = new AbortController();
      const { signal } = graphAbortRef.current;

      setGraphLoading(true);
      try {
        const { startDate, endDate } = periodToDateRange(period);
        const groupBy = periodToGroupBy(period);
        const iccidParam = buildIccidParam(searchText, simCards, friendlyByIccid);

        const bucketRes = await ApiClient.get<{ buckets: VrsBucketRaw[] }>(
          `/vrs/usage/buckets?startDate=${startDate}&endDate=${endDate}&period=${groupBy}${iccidParam}`,
          signal
        );
        setBuckets(
          Array.isArray(bucketRes.buckets) ? bucketRes.buckets.map(normalizeVrsBucket) : []
        );
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('VRS Usage: Graph re-fetch error:', err);
      } finally {
        setGraphLoading(false);
      }
    }, 300);

    return () => {
      if (graphDebounceRef.current) clearTimeout(graphDebounceRef.current);
    };
    // eslint-disable-next-line
  }, [searchText]);

  const filteredSummary = useMemo(() => {
    const summaryByIccid = new Map(summary.map(s => [s.iccid, s]));
    const pool = simCards.map(
      sim =>
        summaryByIccid.get(sim.iccid) || {
          iccid: sim.iccid,
          msisdn: sim.msisdn,
          alias1: sim.alias1,
          status: sim.status,
          total_data_mb: '0',
          total_data_gb: '0',
          total_data_bytes: 0,
          days_with_usage: 0,
          first_usage: '',
          last_usage: '',
        }
    );

    if (!searchText) return pool;
    const q = searchText.toLowerCase();
    return pool.filter(
      s =>
        s.iccid.toLowerCase().includes(q) ||
        (s.alias1 || '').toLowerCase().includes(q) ||
        (friendlyByIccid[s.iccid]?.friendlyName || '').toLowerCase().includes(q)
    );
  }, [summary, simCards, searchText, friendlyByIccid]);

  // Aggregate stats from filteredSummary so tiles respond to search/filter
  const stats = useMemo(() => {
    return {
      totalDataKb:
        filteredSummary.reduce((sum, s) => sum + (Number(s.total_data_bytes) || 0), 0) / 1024,
      simsWithUsage: filteredSummary.filter(s => s.total_data_bytes > 0).length,
      totalSims: filteredSummary.length,
      activeSims: filteredSummary.filter(s => s.status === 'active').length,
    };
  }, [filteredSummary]);

  const statCards: StatCardItem[] = useMemo(
    () => [
      {
        title: 'Total Data',
        value: formatKilobytes(stats.totalDataKb).value,
        suffix: formatKilobytes(stats.totalDataKb).unit,
        icon: <CloudDownloadOutlined />,
      },
      {
        title: 'SIMs with Usage',
        value: stats.simsWithUsage,
        icon: <WifiOutlined />,
      },
      {
        title: 'Total SIM Cards',
        value: stats.totalSims,
        icon: <PhoneOutlined />,
      },
      {
        title: 'Active SIMs',
        value: stats.activeSims,
        icon: <CheckCircleOutlined />,
      },
    ],
    [stats]
  );

  const summaryColumns = [
    {
      title: 'Vessel / Alias',
      dataIndex: 'alias1',
      key: 'alias1',
      sorter: (a: VrsUsageSummary, b: VrsUsageSummary) =>
        (a.alias1 || '').localeCompare(b.alias1 || ''),
      render: (alias: string | null) => <span style={blurStyle(redactNames)}>{alias || '—'}</span>,
    },
    {
      title: 'Friendly Name',
      key: 'friendlyName',
      width: 200,
      sorter: (a: VrsUsageSummary, b: VrsUsageSummary) =>
        (friendlyByIccid[a.iccid]?.friendlyName || '').localeCompare(
          friendlyByIccid[b.iccid]?.friendlyName || ''
        ),
      render: (_: any, record: VrsUsageSummary) => {
        const entry = friendlyByIccid[record.iccid];
        return (
          <EditableFriendlyName
            assignmentId={entry?.assignmentId}
            value={entry?.friendlyName}
            canEdit={!!entry}
            blurred={redactNames}
            onSaved={next =>
              entry &&
              setFriendlyByIccid(prev => ({
                ...prev,
                [record.iccid]: { ...entry, friendlyName: next },
              }))
            }
          />
        );
      },
    },
    {
      title: 'ICCID',
      dataIndex: 'iccid',
      key: 'iccid',
      responsive: ['md'] as Breakpoint[],
      sorter: (a: VrsUsageSummary, b: VrsUsageSummary) => a.iccid.localeCompare(b.iccid),
      render: (iccid: string) => <Link to={`/admin/sims/vrs/${iccid}`}>{iccid}</Link>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Suspended', value: 'suspended' },
      ],
      onFilter: (value: any, record: VrsUsageSummary) => record.status === value,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'suspended' ? 'orange' : 'default'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Data Usage',
      key: 'data',
      sorter: (a: VrsUsageSummary, b: VrsUsageSummary) =>
        (a.total_data_bytes || 0) - (b.total_data_bytes || 0),
      defaultSortOrder: 'descend' as const,
      render: (_: any, record: VrsUsageSummary) => {
        const d = formatKilobytes((Number(record.total_data_bytes) || 0) / 1024);
        return (
          <Text strong>
            {d.value} {d.unit}
          </Text>
        );
      },
    },
    {
      title: 'Days Active',
      dataIndex: 'days_with_usage',
      key: 'days_with_usage',
      responsive: ['sm'] as Breakpoint[],
      sorter: (a: VrsUsageSummary, b: VrsUsageSummary) =>
        (a.days_with_usage || 0) - (b.days_with_usage || 0),
    },
    {
      title: 'Last Usage',
      dataIndex: 'last_usage',
      key: 'last_usage',
      responsive: ['sm'] as Breakpoint[],
      sorter: (a: VrsUsageSummary, b: VrsUsageSummary) =>
        (a.last_usage || '').localeCompare(b.last_usage || ''),
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
  ];

  const currentPeriodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || String(period);

  return (
    <Spin spinning={loading}>
      <PageHeader
        title="Data SIMs"
        exportConfig={{
          filename: () => {
            const periodLabel =
              PERIOD_OPTIONS.find(p => p.value === period)?.label || String(period);
            return `MIMO-DataSIMs-${periodLabel.replace(/\s+/g, '-')}`;
          },
          disabled: loading,
          onBeforePrint: () => setPrintMode(true),
          onAfterPrint: () => setPrintMode(false),
          prePrintDelayMs: 500, // Give the table time to re-render with all rows
        }}
        actions={
          <PageHelp
            title="Data SIMs Help"
            sections={[
              {
                title: 'Overview',
                content:
                  'This page shows data usage for Data maritime SIM cards. Each row represents a SIM card with its total usage over the selected period.',
              },
              {
                title: 'Filters',
                content:
                  'Period: choose time range (7 days, 30 days, 90 days, all)\nSearch: find by ICCID or vessel alias',
              },
              {
                title: 'Table Columns',
                content:
                  'Vessel/Alias: the SIM card name from the carrier\nData Usage: total data transferred in the period\nDays Active: number of days with recorded usage\nLast Usage: most recent date with data activity',
              },
            ]}
          />
        }
      />
      <div className="print-only">
        <h2 className="print-page-title">MIMO Connect — Data SIMs Report</h2>
        <p className="print-meta">Generated {new Date().toLocaleString()}</p>
        <div className="print-filter-summary">
          <strong>Period:</strong>{' '}
          {PERIOD_OPTIONS.find(p => p.value === period)?.label || String(period)}
          {searchText && (
            <>
              {' '}
              {' | '}
              <strong>Search:</strong> "{searchText}"
            </>
          )}
        </div>
      </div>
      {/* Filters */}
      <Row
        gutter={[16, 16]}
        style={{ marginBottom: 16, alignItems: 'center' }}
        className="no-print"
      >
        <Col xs={24} sm={24} md={8}>
          <Select<Period> value={period} onChange={v => setPeriod(v)} style={{ width: '100%' }}>
            {PERIOD_OPTIONS.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={16}>
          <Input.Search
            placeholder="Search ICCID, alias, or friendly name..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
      </Row>
      {/* Stat Cards */}
      <div style={{ marginBottom: 16 }}>
        <StatCardGrid stats={statCards} lgColumns={4} />
      </div>
      {/* Usage Summary Table */}
      <Card title="Data Usage by SIM Card" style={{ marginBottom: 16 }}>
        <Table
          columns={summaryColumns}
          dataSource={filteredSummary}
          rowKey="iccid"
          loading={loading}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  searchText ? 'No SIMs match your filters' : 'No usage data for this period'
                }
              />
            ),
          }}
          pagination={
            printMode
              ? false
              : {
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  responsive: true,
                  showTotal: (total: number, range: [number, number]) =>
                    `${range[0]}-${range[1]} of ${total}`,
                }
          }
          size="small"
        />
      </Card>
      <Card title={`Usage Over Time — ${currentPeriodLabel}`} style={{ marginTop: 16 }}>
        <Spin spinning={graphLoading}>
          <SimUsageGraph buckets={buckets} period={period} metrics={['data']} />
        </Spin>
      </Card>
    </Spin>
  );
}

export default VrsUsage;
